import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { DateRange } from '@/api/generated/schemas';
import type { RangeState } from './publicationTimeline';

/**
 * The operator picks inclusive calendar days; the backend stores a
 * half-open `[lower, upper)` tstzrange. Conversion lives here so it can
 * be tested without a DOM — the repo has no RTL.
 *
 * Observed against the backend on 2026-08-05: it stores the exact
 * instant it is sent, with no server-side date normalization. Picking
 * 31.12.2026 as a single day round-trips as
 * `[2026-12-30T23:00:00Z, 2026-12-31T23:00:00Z)` — local midnight to
 * local midnight in CET. `startOf('day')` is local, so the wire values
 * carry whatever UTC offset applies on that date; a range spanning the
 * October transition legitimately mixes +02:00 and +01:00 and still
 * lands on the picked calendar days.
 *
 * Everything that displays a stored upper bound must therefore shift it
 * back a day — see `inclusiveUpper`.
 */

export type PickerValue = [Dayjs | null, Dayjs | null] | null;

/**
 * Stored exclusive upper bound → the last day actually covered.
 * Returns null for an open-ended window.
 */
export const inclusiveUpper = (
  upper: string | null | undefined
): Dayjs | null =>
  upper ? dayjs(upper).subtract(1, 'day').startOf('day') : null;

/** Stored range → inclusive days for the RangePicker. */
export const rangeToPickerValue = (
  range: DateRange | null | undefined
): PickerValue => {
  if (!range || (!range.lower && !range.upper)) return null;
  return [range.lower ? dayjs(range.lower) : null, inclusiveUpper(range.upper)];
};

/** Inclusive days from the RangePicker → stored half-open range. */
export const pickerValueToRange = (value: PickerValue): DateRange | null => {
  const [lower, upper] = value ?? [];
  if (!lower && !upper) return null;
  return {
    lower: lower ? lower.startOf('day').toISOString() : null,
    upper: upper ? upper.add(1, 'day').startOf('day').toISOString() : null,
  };
};

export type WindowProblem = 'empty' | 'inverted';

/**
 * Why a picked window cannot be saved, or null if it can. Equal days
 * are valid — that is a one-day window — so the rule is applied to the
 * half-open bounds the backend actually receives, which must be a
 * non-empty interval.
 */
export const windowProblem = (value: PickerValue): WindowProblem | null => {
  const range = pickerValueToRange(value);
  if (!range?.lower) return 'empty';
  if (range.upper && !dayjs(range.lower).isBefore(range.upper)) {
    return 'inverted';
  }
  return null;
};

/**
 * State of the window in inclusive-day terms — comparing against the
 * raw upper bound would keep a window "active" on the day after it
 * ends.
 */
export const exclusivityState = (
  range: DateRange | null | undefined,
  now: Dayjs
): RangeState | null => {
  const days = rangeToPickerValue(range);
  if (!days) return null;
  const [lower, upper] = days;
  if (lower && now.isBefore(lower, 'day')) return 'planned';
  if (upper && now.isAfter(upper, 'day')) return 'ended';
  return 'active';
};

const overlaps = (a: DateRange, b: DateRange): boolean => {
  const aStartsAfterBEnds =
    !!a.lower && !!b.upper && !dayjs(a.lower).isBefore(b.upper);
  const bStartsAfterAEnds =
    !!b.lower && !!a.upper && !dayjs(b.lower).isBefore(a.upper);
  return !aStartsAfterBEnds && !bStartsAfterAEnds;
};

/**
 * Whether a proposed exclusivity window falls entirely outside every
 * availability window. Informational only — the operator is not blocked.
 */
export const isOutsideAvailability = (
  window: DateRange | null,
  availableRanges: DateRange[] | null | undefined
): boolean => {
  if (!window) return false;
  if (!availableRanges?.length) return false;
  return !availableRanges.some((range) => overlaps(window, range));
};
